import { AlertTriangle, CheckCircle2, ChevronDown, Info, Loader2, Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as EventoDeTeclado, type ReactNode, type SelectHTMLAttributes } from 'react'
import { createPortal } from 'react-dom'

import { conAyuda } from './Ayuda'

/**
 * El sistema de componentes del panel. Son pocos y se usan en todas partes: la
 * coherencia sale de aquí, no de que cada pantalla se acuerde de las mismas clases
 * (docs/panel-propio.md §3). Ninguno escribe un color: todos salen de los tokens de
 * `estilos.css`.
 *
 * Escala: tipografía 12/13/14/16/20/28; relleno 8/12/16/20/24; radios 8 (botones y
 * campos) y 12 (tarjetas); una sombra suave por omisión y una alta para lo flotante.
 */
export const clases = (...partes: Array<false | null | string | undefined>) => partes.filter(Boolean).join(' ')

// --- Botones ---------------------------------------------------------------------------

type TipoDeBoton = 'discreto' | 'peligro' | 'primario' | 'secundario'

const BOTON: Record<TipoDeBoton, string> = {
  primario: 'bg-violeta text-white shadow-suave hover:bg-violeta-2 disabled:bg-linea-fuerte disabled:text-tenue disabled:shadow-none',
  secundario: 'border border-linea bg-papel text-tinta shadow-suave hover:bg-papel-2 hover:border-linea-fuerte disabled:text-tenue disabled:shadow-none',
  discreto: 'text-tinta-2 hover:bg-papel-3 hover:text-tinta disabled:text-tenue',
  peligro: 'text-error hover:bg-error-suave disabled:text-tenue',
}

export const Boton = ({
  tipo = 'secundario',
  pequeno = false,
  icono,
  cargando = false,
  children,
  className,
  disabled,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & { cargando?: boolean; icono?: ReactNode; pequeno?: boolean; tipo?: TipoDeBoton }) => (
  <button
    className={clases(
      'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-boton font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed',
      pequeno ? 'h-8 px-3 text-[13px]' : 'h-9 px-3.5 text-sm',
      BOTON[tipo],
      className,
    )}
    disabled={disabled || cargando}
    type="button"
    {...resto}
  >
    {cargando ? <Loader2 aria-hidden className="size-4 animate-spin" /> : icono ? <span aria-hidden className="[&>svg]:size-4">{icono}</span> : null}
    {children}
  </button>
)

/** Un botón que es solo un icono: siempre con `aria-label`. */
export const BotonDeIcono = ({
  etiqueta,
  children,
  pequeno = false,
  tipo = 'discreto',
  className,
  ...resto
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode; etiqueta: string; pequeno?: boolean; tipo?: TipoDeBoton }) => (
  <button
    aria-label={etiqueta}
    className={clases(
      'inline-flex shrink-0 items-center justify-center rounded-boton transition-colors disabled:cursor-not-allowed [&>svg]:size-4',
      pequeno ? 'size-7' : 'size-8',
      BOTON[tipo],
      tipo === 'secundario' && 'shadow-none',
      className,
    )}
    title={etiqueta}
    type="button"
    {...resto}
  >
    {children}
  </button>
)

// --- Campos ----------------------------------------------------------------------------

/** La clase de todo control de texto: la misma en el editor, la barra de búsqueda y los filtros. */
export const claseDeEntrada = (error?: string, pequeno = false) =>
  clases(
    'campo w-full rounded-campo border bg-papel text-tinta placeholder:text-tenue transition-colors disabled:bg-papel-2 disabled:text-tenue',
    pequeno ? 'h-8 px-2.5 text-[13px]' : 'h-9 px-3 text-sm',
    error ? 'border-error' : 'border-linea hover:border-linea-fuerte',
  )

export const Campo = ({ etiqueta, ayuda, error, className, ...resto }: InputHTMLAttributes<HTMLInputElement> & { ayuda?: string; error?: string; etiqueta: string }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[13px] font-medium text-tinta">{etiqueta}</span>
    <input className={clases(claseDeEntrada(error), className)} {...resto} />
    {ayuda && !error && <span className="text-xs text-tenue">{ayuda}</span>}
    {error && (
      <span className="text-xs text-error" role="alert">
        {error}
      </span>
    )}
  </label>
)

/**
 * Una casilla, con su estado «a medias» (la de un grupo con algunos marcados): el nativo lo
 * tiene, pero solo por JavaScript (`indeterminate`), y así lo anuncian los lectores de
 * pantalla. El color sale del token de marca (`accent-color`).
 */
export const Casilla = ({
  marcada,
  aMedias = false,
  etiqueta,
  className,
  ...resto
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'checked' | 'type'> & { aMedias?: boolean; etiqueta?: string; marcada: boolean }) => {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = aMedias
  }, [aMedias])
  return (
    <input
      aria-label={etiqueta}
      checked={marcada}
      className={clases('size-4 shrink-0 cursor-pointer accent-violeta disabled:cursor-not-allowed', className)}
      ref={ref}
      type="checkbox"
      {...resto}
    />
  )
}

/** Un `<select>` con la flecha nuestra: el nativo pinta la suya de forma distinta en cada sistema. */
export const Selector = ({ className, pequeno = false, children, ...resto }: SelectHTMLAttributes<HTMLSelectElement> & { pequeno?: boolean }) => (
  <span className="relative inline-flex w-full">
    <select className={clases(claseDeEntrada(undefined, pequeno), 'appearance-none pr-8', className)} {...resto}>
      {children}
    </select>
    <ChevronDown aria-hidden className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-tenue" />
  </span>
)

/** Búsqueda que busca sola: al escribir (con un respiro) o al pulsar Intro. Sin botón «Buscar». */
export const BarraDeBusqueda = ({
  valor,
  alBuscar,
  placeholder = 'Buscar…',
  className,
}: {
  alBuscar: (texto: string) => void
  className?: string
  placeholder?: string
  valor: string
}) => {
  const [texto, setTexto] = useState(valor)
  const temporizador = useRef<number | null>(null)
  useEffect(() => setTexto(valor), [valor])
  const emitir = (t: string) => {
    if (temporizador.current) window.clearTimeout(temporizador.current)
    alBuscar(t)
  }
  return (
    <div className={clases('relative', className)}>
      <Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue" />
      <input
        aria-label="Buscar"
        className={clases(claseDeEntrada(), 'pr-8 pl-9')}
        onChange={(e) => {
          const t = e.target.value
          setTexto(t)
          if (temporizador.current) window.clearTimeout(temporizador.current)
          temporizador.current = window.setTimeout(() => alBuscar(t), 350)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') emitir(texto)
          if (e.key === 'Escape') {
            setTexto('')
            emitir('')
          }
        }}
        placeholder={placeholder}
        type="search"
        value={texto}
      />
      {texto && (
        <button
          aria-label="Borrar la búsqueda"
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-tenue hover:text-tinta"
          onClick={() => {
            setTexto('')
            emitir('')
          }}
          type="button"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}

// --- Avisos y estados ----------------------------------------------------------------

type TonoDeAviso = 'aviso' | 'error' | 'info' | 'ok'

const AVISO: Record<TonoDeAviso, { clase: string; icono: ReactNode }> = {
  ok: { clase: 'border-ok/30 bg-ok-suave text-tinta', icono: <CheckCircle2 className="size-4 text-ok" /> },
  aviso: { clase: 'border-aviso/30 bg-aviso-suave text-tinta', icono: <AlertTriangle className="size-4 text-aviso" /> },
  error: { clase: 'border-error/30 bg-error-suave text-tinta', icono: <AlertTriangle className="size-4 text-error" /> },
  info: { clase: 'border-linea bg-papel-2 text-tinta', icono: <Info className="size-4 text-tenue" /> },
}

/** Un aviso dice **qué hacer**, no qué ha fallado (§28 de CLAUDE.md). El texto viene casi siempre del núcleo. */
export const Aviso = ({ tono = 'aviso', children }: { children: ReactNode; tono?: TonoDeAviso }) => (
  <div className={clases('flex items-start gap-2.5 rounded-caja border px-3.5 py-3 text-sm', AVISO[tono].clase)} role={tono === 'error' ? 'alert' : 'status'}>
    <span aria-hidden className="mt-0.5 shrink-0">
      {AVISO[tono].icono}
    </span>
    <p className="min-w-0 flex-1">{conAyuda(children)}</p>
  </div>
)

export const Cargando = ({ que = 'Cargando' }: { que?: string }) => (
  <p className="flex items-center gap-2.5 py-10 text-sm text-tenue" role="status">
    <Loader2 aria-hidden className="size-4 animate-spin text-violeta" />
    {que}…
  </p>
)

/** Filas grises que ocupan el sitio de lo que está llegando: la pantalla no salta al cargar. */
export const Esqueleto = ({ filas = 5 }: { filas?: number }) => (
  <div aria-hidden className="flex flex-col gap-3 p-5">
    {Array.from({ length: filas }, (_, i) => (
      <div className="h-4 animate-pulse rounded bg-papel-3" key={i} style={{ width: `${70 - (i % 3) * 15}%` }} />
    ))}
  </div>
)

export const Tarjeta = ({ children, className, sinRelleno = false }: { children: ReactNode; className?: string; sinRelleno?: boolean }) => (
  <div className={clases('rounded-caja border border-linea bg-papel shadow-suave', !sinRelleno && 'p-5', className)}>{children}</div>
)

type ClaveDePastilla = 'construyendo' | 'fallida' | 'neutra' | 'publicado' | 'sin-publicar' | 'violeta'

const PASTILLA: Record<ClaveDePastilla, string> = {
  publicado: 'border-ok/25 bg-ok-suave text-ok',
  construyendo: 'border-aviso/25 bg-aviso-suave text-aviso',
  fallida: 'border-error/25 bg-error-suave text-error',
  'sin-publicar': 'border-linea bg-papel-2 text-tinta-2',
  neutra: 'border-linea bg-papel-2 text-tinta-2',
  violeta: 'border-violeta-borde bg-violeta-suave text-violeta',
}

export const Pastilla = ({ clave, children, punto = true }: { children: ReactNode; clave: ClaveDePastilla; punto?: boolean }) => (
  <span className={clases('inline-flex items-center gap-1.5 rounded-pastilla border px-2 py-0.5 text-xs font-medium whitespace-nowrap', PASTILLA[clave])}>
    {punto && clave !== 'neutra' && clave !== 'violeta' && <span aria-hidden className="size-1.5 rounded-full bg-current" />}
    {children}
  </span>
)

/** La cabecera de una página: rótulo pequeño, título, descripción y las acciones a la derecha. */
export const Titulo = ({
  rotulo,
  children,
  descripcion,
  accion,
  volver,
}: {
  accion?: ReactNode
  children: ReactNode
  descripcion?: ReactNode
  rotulo?: ReactNode
  volver?: ReactNode
}) => (
  <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div className="min-w-0">
      {volver}
      {rotulo && <p className="mb-1 text-xs font-medium tracking-wide text-tenue uppercase">{rotulo}</p>}
      <h1 className="font-display text-[26px] leading-tight font-semibold tracking-tight">{children}</h1>
      {descripcion && <p className="mt-1.5 text-sm text-tenue">{descripcion}</p>}
    </div>
    {accion && <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{accion}</div>}
  </header>
)

/** Un listado sin nada: dice qué es, por qué está vacío y qué hacer, en vez de una frase suelta. */
export const EstadoVacio = ({ icono, titulo, descripcion, accion }: { accion?: ReactNode; descripcion?: ReactNode; icono?: ReactNode; titulo: ReactNode }) => (
  <div className="flex flex-col items-center justify-center rounded-caja border border-dashed border-linea-fuerte bg-papel px-6 py-14 text-center">
    {icono && <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-violeta-suave text-violeta [&>svg]:size-6">{icono}</div>}
    <h2 className="font-display text-lg font-semibold">{titulo}</h2>
    {descripcion && <p className="mt-1.5 max-w-md text-sm text-tenue">{descripcion}</p>}
    {accion && <div className="mt-5">{accion}</div>}
  </div>
)

const ENFOCABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Un diálogo modal: fondo atenuado, tarjeta centrada, cerrar con el botón, con Escape o
 * pulsando fuera. El foco entra al abrirlo —al control marcado con `data-autofocus`, o al
 * diálogo—, no se escapa con Tab mientras está abierto y vuelve a donde estaba al cerrarlo:
 * se navega con teclado más de lo que la gente cree.
 */
export const Modal = ({
  titulo,
  cerrar,
  children,
  ancho = 'max-w-4xl',
  pie,
}: {
  ancho?: string
  cerrar: () => void
  children: ReactNode
  pie?: ReactNode
  titulo: ReactNode
}) => {
  const caja = useRef<HTMLDivElement>(null)
  const idDelTitulo = useId()
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && cerrar()
    document.addEventListener('keydown', alTeclear)
    return () => document.removeEventListener('keydown', alTeclear)
  }, [cerrar])
  useEffect(() => {
    const antes = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const primero = caja.current?.querySelector<HTMLElement>('[data-autofocus]')
    ;(primero ?? caja.current)?.focus()
    return () => antes?.focus()
  }, [])
  const retenerElFoco = (e: EventoDeTeclado<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !caja.current) return
    const enfocables = [...caja.current.querySelectorAll<HTMLElement>(ENFOCABLES)].filter((el) => el.offsetParent !== null || el === document.activeElement)
    if (enfocables.length === 0) return
    const primero = enfocables[0]!
    const ultimo = enfocables[enfocables.length - 1]!
    if (e.shiftKey && (document.activeElement === primero || document.activeElement === caja.current)) {
      e.preventDefault()
      ultimo.focus()
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault()
      primero.focus()
    }
  }
  // Se pinta en el `body`, no donde se declara: un antecesor con `backdrop-filter`,
  // `transform` o `filter` (la cabecera lleva `backdrop-blur`) se convierte en el
  // contenedor de lo `fixed`, y el diálogo quedaba encerrado en sus 56 px de alto.
  return createPortal(
    <div
      aria-labelledby={idDelTitulo}
      aria-modal
      className="fixed inset-0 z-50 flex items-center justify-center bg-velo/50 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && cerrar()}
      role="dialog"
    >
      <div className={clases('flex max-h-[90vh] w-full flex-col overflow-hidden rounded-caja border border-linea bg-papel shadow-alta outline-none', ancho)} onKeyDown={retenerElFoco} ref={caja} tabIndex={-1}>
        <div className="flex items-center justify-between gap-4 border-b border-linea px-5 py-3.5">
          <h2 className="font-display text-base font-semibold" id={idDelTitulo}>
            {titulo}
          </h2>
          <BotonDeIcono etiqueta="Cerrar" onClick={cerrar} pequeno>
            <X />
          </BotonDeIcono>
        </div>
        <div className="overflow-y-auto p-5">{children}</div>
        {pie && <div className="flex justify-end gap-2 border-t border-linea px-5 py-3.5">{pie}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** Pestañas subrayadas, las mismas en el editor y donde haga falta. */
export const Pestanas = ({ pestanas, activa, alCambiar }: { activa: string; alCambiar: (clave: string) => void; pestanas: Array<{ clave: string; etiqueta: ReactNode; aviso?: boolean }> }) => (
  <div className="flex flex-wrap gap-1 border-b border-linea" role="tablist">
    {pestanas.map((p) => (
      <button
        aria-selected={activa === p.clave}
        className={clases(
          '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm transition-colors',
          activa === p.clave ? 'border-violeta font-medium text-tinta' : 'border-transparent text-tenue hover:text-tinta',
        )}
        key={p.clave}
        onClick={() => alCambiar(p.clave)}
        role="tab"
        type="button"
      >
        {p.etiqueta}
        {p.aviso && <span aria-label="con errores" className="size-1.5 rounded-full bg-error" />}
      </button>
    ))}
  </div>
)
